import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDistributionType } from "./enums/enumDistributionType.generated";

// Model Objects

export class WooCommerceShippingMappingReadOnly extends ModelObject<WooCommerceShippingMappingReadOnly> {

    /**
    * The woo commerce settings which this mapping belongs to.
    */
    public wooCommerceSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.wooCommerceSettingsId = Guid.parseOrEmpty(jsonSerializedObject.wooCommerceSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.wooCommerceSettingsId = this.wooCommerceSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "wooCommerceSettingsId", rule: (v: any) => !v.isEmpty() || "WooCommerceShippingMappingReadOnly.WooCommerceSettingsId cannot be empty"},
    ];
}


export class WooCommerceShippingMapping extends ModelObject<WooCommerceShippingMapping> {

    /**
    * The woo commerce shipping method name.
    * Not Nullable
    */
    public wooCommerceShippingMethodName: string = "";
    /**
    * The Brewman distribution type to use for orders with this shipping method.
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area Id, required if the distribution type is delivery.
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier Id, required if the distribution type is courier.
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    public readOnly: WooCommerceShippingMappingReadOnly = new WooCommerceShippingMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.wooCommerceShippingMethodName = jsonSerializedObject.wooCommerceShippingMethodName;
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.readOnly = new WooCommerceShippingMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.wooCommerceShippingMethodName = this.wooCommerceShippingMethodName;
        toRet.distributionType = this.distributionType.value;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "WooCommerceShippingMapping.DeliveryAreaId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "WooCommerceShippingMapping.CourierId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class WooCommerceSettingsReadOnly extends ModelObject<WooCommerceSettingsReadOnly> {

    public apiConsumerSecretLength: number = 0;
    private _webhookOrderCreatedId: number | null = null;
    /**
    * Woocommerce webhook id registered for order.created webhook.This id is needed to unregister.
    */
    public get webhookOrderCreatedId(): number | null { return this._webhookOrderCreatedId }
    public set webhookOrderCreatedId(value: number | null) {
      this._webhookOrderCreatedId = value === undefined ? null : value;
    }
    private _webhookOrderUpdatedId: number | null = null;
    /**
    * Woocommerce webhook id registered for order.updated webhook.This id is needed to unregister.
    */
    public get webhookOrderUpdatedId(): number | null { return this._webhookOrderUpdatedId }
    public set webhookOrderUpdatedId(value: number | null) {
      this._webhookOrderUpdatedId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.apiConsumerSecretLength = jsonSerializedObject.apiConsumerSecretLength;
          this.webhookOrderCreatedId = jsonSerializedObject.webhookOrderCreatedId == null ? null : jsonSerializedObject.webhookOrderCreatedId;
          this.webhookOrderUpdatedId = jsonSerializedObject.webhookOrderUpdatedId == null ? null : jsonSerializedObject.webhookOrderUpdatedId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.apiConsumerSecretLength = this.apiConsumerSecretLength;
        toRet.webhookOrderCreatedId = this.webhookOrderCreatedId == null ? null : this.webhookOrderCreatedId;
        toRet.webhookOrderUpdatedId = this.webhookOrderUpdatedId == null ? null : this.webhookOrderUpdatedId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "apiConsumerSecretLength", rule: (v: any) => v == null || v >= -2147483648 || "ApiConsumerSecretLength is not greater than minimum allowed value: -2147483648"},
            {field: "apiConsumerSecretLength", rule: (v: any) => v == null || v <= 2147483647 || "ApiConsumerSecretLength is above maximum allowed value: 2147483647"},
            {field: "webhookOrderCreatedId", rule: (v: any) => v == null || v >= -2147483648 || "WebhookOrderCreatedId is not greater than minimum allowed value: -2147483648"},
            {field: "webhookOrderCreatedId", rule: (v: any) => v == null || v <= 2147483647 || "WebhookOrderCreatedId is above maximum allowed value: 2147483647"},
            {field: "webhookOrderUpdatedId", rule: (v: any) => v == null || v >= -2147483648 || "WebhookOrderUpdatedId is not greater than minimum allowed value: -2147483648"},
            {field: "webhookOrderUpdatedId", rule: (v: any) => v == null || v <= 2147483647 || "WebhookOrderUpdatedId is above maximum allowed value: 2147483647"},
    ];
}


export class WooCommerceSettings extends ModelObject<WooCommerceSettings> {

    /**
    * The Api Consumer Key provided by the WooCommerce instance to be used for the link.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public apiConsumerKey: string = "";
    public id: Guid = Guid.createEmpty();
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Method Name in WooCommerce
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id. Required in order to perform import. Should be Null only if WooCommerce is not in use for the tenant.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    /**
    * The base Url of the WooCommerce store.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public storeUrl: string = "";
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    public readOnly: WooCommerceSettingsReadOnly = new WooCommerceSettingsReadOnly();
    public version: number = 0;
    public shipingMappings: (WooCommerceShippingMapping)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.apiConsumerKey = jsonSerializedObject.apiConsumerKey;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.storeUrl = jsonSerializedObject.storeUrl;
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.readOnly = new WooCommerceSettingsReadOnly(jsonSerializedObject.readOnly);
          this.version = jsonSerializedObject.version;
          this.shipingMappings = jsonSerializedObject.shipingMappings.map((v: any) => new WooCommerceShippingMapping(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.apiConsumerKey = this.apiConsumerKey;
        toRet.id = this.id.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.storeUrl = this.storeUrl;
        toRet.runInBackground = this.runInBackground;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.version = this.version;
        toRet.shipingMappings = this.shipingMappings.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "WooCommerceSettings.Id cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "WooCommerceSettings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "WooCommerceSettings.OutletId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "shipingMappings", rule: (v: any) => !!v || "shipingMappings is required"},
            {field: "shipingMappings", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as WooCommerceShippingMapping[]).reduce((acc: Array<string>, curr: WooCommerceShippingMapping) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class TenantSettingsId extends ModelObject<TenantSettingsId> {

    public tenantId: Guid = Guid.createEmpty();
    public wooCommerceSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.wooCommerceSettingsId = Guid.parseOrEmpty(jsonSerializedObject.wooCommerceSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.wooCommerceSettingsId = this.wooCommerceSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.TenantId cannot be empty"},
            {field: "wooCommerceSettingsId", rule: (v: any) => !v.isEmpty() || "TenantSettingsId.WooCommerceSettingsId cannot be empty"},
    ];
}


export class WooCommerceSettingsService extends ServiceBase {

  public async getWooCommerceSettings(wooCommerceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<WooCommerceSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    wooCommerceSettingsId:wooCommerceSettingsId.toString(),
    };
    const extractor = (response:any) => new WooCommerceSettings(response.wooCommerceSettings);;
    return this.makeJsonRequest("wooCommerceSettings/v1/getWooCommerceSettings", req, false, extractor, opts);
  };

  public async getAllWooCommerceSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(WooCommerceSettings)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.wooCommerceSettings.map((v: any) => new WooCommerceSettings(v));;
    return this.makeJsonRequest("wooCommerceSettings/v1/getAllWooCommerceSettings", req, false, extractor, opts);
  };

  public async saveWooCommerceSettings(wooCommerceSettings: WooCommerceSettings, apiConsumerSecret: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    wooCommerceSettings:wooCommerceSettings.toJsonSerializedObject(),
    apiConsumerSecret:apiConsumerSecret == null ? null : apiConsumerSecret,
    };
    const extractor = null;
    return this.makeJsonRequest("wooCommerceSettings/v1/saveWooCommerceSettings", req, true, extractor, opts);
  };

  public async hardDeleteWooCommerceSettings(wooCommerceSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    wooCommerceSettingsId:wooCommerceSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("wooCommerceSettings/v1/hardDeleteWooCommerceSettings", req, true, extractor, opts);
  };
}

export const wooCommerceSettingsService = new WooCommerceSettingsService();